import {
  getPreferencesSetting,
  savePreferencesSetting,
} from '@/apis/materialList'
import { headPropList } from '@/components/materialTable/src/config/tableConfig'
import store from '@/store'
import { TaskStatus, createTask } from '@/store/modules/progressNotice'
import { EventBusKey } from '@/util/bus'
import EventBus from '@/util/bus'
import { translate } from '@/util/commonFun'
import { message } from 'ant-design-vue'
import { debounce } from 'lodash'
import type { Module } from 'vuex'

import type { IRootState } from '../../types'
import type { IStore, PreferencesSetting } from './types'

const preferencesSetting: Module<IStore, IRootState> = {
  namespaced: true,
  state: {
    setting: {
      layoutStoreType: 'awaitStore',
      isDeductionBujian: false,
      isDeductionPaibanStore: false,
      isDeductionSurplus: false,
      isDeductionBaseMaterial: true,
      genSmallPlankLabel: true,
      labelSortWay: 'default',
      exNextTip: true,
      needSaveData: JSON.parse(JSON.stringify(headPropList)),
    },
    isLoading: true,
    isLoadDone: false,
  },
  mutations: {
    setSetting(state, val) {
      state.setting = val
    },
    setIsLoading(state, val) {
      state.isLoading = val
    },
    setIsLoadDone(state, val) {
      state.isLoadDone = val
    },
  },
  actions: {
    async updateSetting({ state }) {
      if (!state.setting || !Object.keys(state).length) return
      await savePreferencesSetting(state.setting as any)
    },
    /** 此方法不可轻易调用 */
    async getSetting({ commit, state }) {
      const {
        task,
        switchTaskStatus,
        delTask,
        data: taskData,
      } = createTask(
        {
          taskTitle: translate('common.preferenceLoadTip'),
          isAutoClose: false,
          errorMsg: translate('common.preferenceLoadErrTip'),
        },
        1
      )
      try {
        commit('setIsLoading', true)
        const result = await getPreferencesSetting(
          window.sessionStorage.getItem('thinkerx_material') as any,
          store.state.userInfo.platform
        )
        commit('setIsLoading', false)
        if (result.status !== 1) {
          taskData.errorMsg = (result.data as any)?.err_info ?? result.msg
          switchTaskStatus(TaskStatus.rejected)
          return
        }
        commit('setIsLoadDone', true)
        const setting = result.data
        const init = (key: keyof IStore['setting'], value: any) => {
          setting[key] = value as never
        }
        // 部分值的初始化
        // isBujianRecommend,isPaibanStoreRecommend,isSurplusRecommend三个数据的初始值要为true
        init('isBujianRecommend', setting.isBujianRecommend ?? true)
        init('isPaibanStoreRecommend', setting.isPaibanStoreRecommend ?? true)
        init('isSurplusRecommend', setting.isSurplusRecommend ?? true)
        init('isDeductionBaseMaterial', !!setting.isDeductionBaseMaterial)
        init('isDeductionBujian', !!setting.isDeductionBujian)
        init('isDeductionPaibanStore', !!setting.isDeductionPaibanStore)
        init('isDeductionSurplus', !!setting.isDeductionSurplus)
        init('genSmallPlankLabel', setting.genSmallPlankLabel ?? true)
        init('exNextTip', setting.exNextTip ?? true)
        setting.needSaveData = setting.needSaveData.map((item) => {
          return {
            ...item,
            customLabel: item.customLabel ?? item.label,
          }
        })
        init(
          'needSaveData',
          setting.needSaveData ?? JSON.parse(JSON.stringify(headPropList))
        )
        init(
          'genSmallPlankLoaelForSurplus',
          setting.genSmallPlankLoaelForSurplus ??
            setting.genSmallPlankLabel ??
            true
        )
        init('isSortByPlankCountDesc', setting.isSortByPlankCountDesc ?? false)
        init('layoutStoreType', setting.layoutStoreType ?? 'awaitStore')
        init('isCustomPaibanImg', setting.isCustomPaibanImg ?? false)
        init('paibanImgTemplateId', setting.paibanImgTemplateId ?? 1)
        init('numAutoOffset', setting.numAutoOffset ?? 1)
        init('onePageNum', setting.onePageNum ?? 1)
        init('stacking_method', setting.stacking_method ?? 'short')
        init(
          'paibanWay',
          setting.paibanWay
            ? setting.paibanWay.replace('_roll', '')
            : 'paiban_v2'
        )
        init(
          'dualLanguageTags',
          setting.dualLanguageTags ?? ['roomName', 'partName', 'loc']
        )
        init(
          'isUsePlankReuseMatchingPlankName',
          setting.isUsePlankReuseMatchingPlankName ?? false
        )
        /** @description 修正之前顺带导出的数据 */
        const initExportType = (type: string | string[]) => {
          if (Array.isArray(type)) return type
          // 之前的数据就是字符串
          if (type === '导出图片') {
            return ['picture']
          } else if (['导出PDF', '导出pdf'].includes(type)) {
            return ['pdf']
          }
          return ['picture']
        }
        init('currentTagType', initExportType(setting.currentTagType))
        init(
          'currentPaibanImgType',
          initExportType(setting.currentPaibanImgType)
        )

        commit('setSetting', setting)
        task(1)
        // 任务完成后清理列表
        delTask()
        EventBus.$emit(EventBusKey.PREFERENCES_UPDATE, {
          data: state.setting,
          isErr: false,
        })
      } catch (error) {
        commit('setIsLoading', false)
        switchTaskStatus(TaskStatus.rejected)
        EventBus.$emit(EventBusKey.PREFERENCES_UPDATE, {
          data: null,
          isErr: true,
        })
      }
    },
  },
}

type PrefCb = (payload: {
  data: IStore['setting'] | undefined
  isErr: boolean
  err?: any
}) => void

/**
 * @description 防抖的更新
 */
const updateServe = debounce(async (cb?: PrefCb) => {
  try {
    // 更新完成后直接更新服务器上的设置
    await store.dispatch('preferencesSetting/updateSetting')
    // 执行一些需要的回调函数
    cb &&
      cb({
        data: store.state.preferencesSetting?.setting,
        isErr: false,
      })
  } catch (error) {
    cb &&
      cb({
        data: undefined,
        isErr: true,
        err: error,
      })
    message.error(translate('common.preferenceSaveErr'))
  }
}, 150)
/**
 * @description 更新本地偏好设置，请不要重复的请求偏好设置接口，当偏好设置保存时请将保存的数据调用一次此方法，用于更新本地数据同时更新远程服务器数据,可只传入想要更新的内部自动会填充之前的数据
 * 这样做的原因：历史遗留问题，偏好设置之前被滥用，导致数据在多个页面多次不同的获取，为了保证改动最小则采用了本地页面更新
 * @param data 需要更新的数据
 * @param isUpdateServe 是否更新远程服务器上的数据,默认更新(留下这个兼容，但基本没有需求会有更新本地但不更新远程)
 */
export function updateLocalPreference(
  data: PreferencesSetting,
  cb?: PrefCb,
  isUpdateServe = true
) {
  const nData: any = {}
  Object.keys(data as any).forEach((k) => {
    if (typeof data[k as keyof PreferencesSetting] !== 'undefined') {
      nData[k] = data[k as keyof PreferencesSetting]
    }
  })
  store.commit('preferencesSetting/setSetting', {
    ...store.state.preferencesSetting?.setting,
    ...nData,
  })
  isUpdateServe && updateServe(cb)
}

export default preferencesSetting
